import React, { useRef, type KeyboardEvent } from 'react';
import { Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { useAutoGrowTextarea } from '@hooks/useAutoGrowTextarea';

interface CADChatComposerProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  onSend: () => void;
  loading: boolean;
}

const CADChatComposer: React.FC<CADChatComposerProps> = ({
  value,
  onChange,
  onKeyDown,
  onSend,
  loading,
}) => {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // MUI's maxRows={3} grew the box to three rows; 4.5rem is that cap.
  useAutoGrowTextarea(textareaRef, value, 72);

  return (
    <div className="flex items-end gap-1 border-t border-border p-1">
      <Textarea
        ref={textareaRef}
        rows={1}
        placeholder={t('cad.chat.placeholder')}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
        disabled={loading}
        className="revy-cjk min-h-9 max-h-[4.5rem] resize-none overflow-y-auto py-1.5 text-[0.8rem] md:text-[0.8rem] leading-[1.5]"
      />
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={t('chat.send')}
        onClick={onSend}
        disabled={!value.trim() || loading}
        className="shrink-0 text-primary"
      >
        {loading ? <Spinner size="sm" label="" /> : <Send strokeWidth={1.9} />}
      </Button>
    </div>
  );
};

export default CADChatComposer;
